import { useId, type ReactElement } from 'react';
import { NavLink } from 'react-router';
import { cx } from '../ui/cx';
import s from './OpsSidebar.module.css';

export interface OpsSidebarItem {
  id: string;
  label: string;
  href: string;
  /** Items in the view. Shown as a number badge; read out with the label. */
  count?: number;
  /** What the count is, read out after it ("new": "My feedback, 1 new"). */
  countName?: string;
  /** `problem` for views that need attention (Past deadline). */
  tone?: 'default' | 'problem';
  end?: boolean;
}

export interface OpsSidebarGroup {
  label: string;
  items: readonly OpsSidebarItem[];
}

export interface OpsSidebarProps {
  /** Names the navigation ("Queue sections"). */
  label: string;
  groups: readonly OpsSidebarGroup[];
  className?: string;
  /** The groups' heading level: 3 when the sidebar sits under a card's own heading. */
  headingLevel?: 2 | 3;
}

/** The left sidebar for long operational lists (Stock Admin and Payroll queues), with counts. */
export function OpsSidebar({
  label,
  groups,
  className,
  headingLevel = 2,
}: OpsSidebarProps): ReactElement {
  const baseId = useId();
  const GroupHeading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <nav aria-label={label} className={cx(s.sidebar, className)}>
      {groups.map((group, index) => {
        const headingId = `${baseId}-${index}`;
        return (
          <div key={group.label}>
            <GroupHeading id={headingId} className={s.groupLabel}>
              {group.label}
            </GroupHeading>
            <ul className={s.group} aria-labelledby={headingId}>
              {group.items.map((item) => (
                <li key={item.id}>
                  <NavLink to={item.href} end={item.end ?? false} className={s.item}>
                    <span>{item.label}</span>
                    {item.count !== undefined && (
                      <span className={cx(s.badge, item.tone === 'problem' && s.problem)}>
                        <span className="visually-hidden">, </span>
                        {item.count}
                        {item.countName !== undefined && (
                          <span className="visually-hidden"> {item.countName}</span>
                        )}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}
